import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, PencilSimple, Trash, DownloadSimple, Phone as PhoneIcon, Envelope } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const API = `${window.location.origin}/api`;
const ROLES = ["Super Admin", "Admin", "Sales Manager", "Sales Executive", "Operations"];
const DEPARTMENTS = ["Sales", "Marketing", "Operations", "Finance", "Support", "Management"];

export default function TeamPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "Sales Executive", department: "Sales", status: "Active" });

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/team`);
      setMembers(res.data);
    } catch (err) { toast.error("Failed to load team members"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchMembers(); }, []);

  const resetForm = () => setForm({ name: "", email: "", phone: "", role: "Sales Executive", department: "Sales", status: "Active" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error("Name required"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/team/${editing.id}`, form);
        toast.success("Member updated!");
      } else {
        await axios.post(`${API}/team`, form);
        toast.success("Member added!");
      }
      setShowDialog(false); setEditing(null); resetForm(); fetchMembers();
    } catch (err) { toast.error("Failed to save"); }
  };

  const handleEdit = (m) => {
    setEditing(m);
    setForm({ name: m.name, email: m.email, phone: m.phone, role: m.role, department: m.department, status: m.status });
    setShowDialog(true);
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Remove ${m.name}?`)) return;
    try { await axios.delete(`${API}/team/${m.id}`); toast.success("Member removed"); fetchMembers(); }
    catch (err) { toast.error("Failed to remove"); }
  };

  const handleExport = () => window.open(`${API}/export/team`, "_blank");

  const roleColors = {
    "Super Admin": "bg-purple-100 text-purple-700",
    "Admin": "bg-indigo-100 text-indigo-700",
    "Sales Manager": "bg-blue-100 text-blue-700",
    "Sales Executive": "bg-cyan-100 text-cyan-700",
    "Operations": "bg-teal-100 text-teal-700",
  };

  return (
    <div className="space-y-5 max-w-6xl" data-testid="team-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Gupter', serif" }}>Team Members</h1>
          <p className="text-sm text-gray-500">{members.length} members</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-team-btn">
            <DownloadSimple size={16} className="mr-1" /> Export
          </Button>
          <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => { resetForm(); setEditing(null); setShowDialog(true); }} data-testid="add-member-btn">
            <Plus size={16} className="mr-1" /> Add Member
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? [1, 2, 3].map((i) => <div key={i} className="h-48 bg-gray-100 rounded-xl animate-pulse" />) :
          members.length === 0 ? (
            <Card className="col-span-full p-12 text-center border border-gray-200 bg-white">
              <p className="text-gray-400 mb-3">No team members yet</p>
              <Button size="sm" className="bg-indigo-600 text-white" onClick={() => setShowDialog(true)}>
                <Plus size={16} className="mr-1" /> Add First Member
              </Button>
            </Card>
          ) : members.map((m) => (
            <Card key={m.id} className="p-5 border border-gray-200 bg-white hover:shadow-md transition-shadow group" data-testid={`member-card-${m.id}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-sm">
                    {m.name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">{m.name}</h3>
                    <p className="text-xs text-gray-500">{m.department}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${m.status === "Active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                  {m.status}
                </span>
              </div>
              <div className="space-y-1.5 text-sm mb-3">
                <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${roleColors[m.role] || "bg-gray-100 text-gray-600"}`}>{m.role}</span>
                {m.email && <p className="text-xs text-gray-500 flex items-center gap-1.5"><Envelope size={12} />{m.email}</p>}
                {m.phone && <p className="text-xs text-gray-500 flex items-center gap-1.5"><PhoneIcon size={12} />{m.phone}</p>}
              </div>
              <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="outline" size="sm" className="flex-1 h-7 text-xs" onClick={() => handleEdit(m)}>
                  <PencilSimple size={12} className="mr-1" /> Edit
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs text-red-500 hover:bg-red-50" onClick={() => handleDelete(m)}>
                  <Trash size={12} />
                </Button>
              </div>
            </Card>
          ))
        }
      </div>

      <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) { setEditing(null); resetForm(); } }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Gupter', serif" }}>{editing ? "Edit Member" : "Add Team Member"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Name *</label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" data-testid="member-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@co.com" data-testid="member-email" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Phone</label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91..." data-testid="member-phone" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Role</label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Department</label>
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="member-submit-btn">
              {editing ? "Update Member" : "Add Member"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
